import {
  Autocomplete,
  AutocompleteProps,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import TextFieldLabeled from "../TextFieldLabeled";

// Helper to derive value type from multiple/freeSolo flags
type ValueType<
  T,
  M extends boolean | undefined,
  F extends boolean | undefined
> = M extends true
  ? F extends true
    ? Array<T | string>
    : T[]
  : F extends true
  ? T | string
  : T | null;

// Base props shared
interface BaseProps<T> {
  loadOptions: () => Promise<T[]>;
  initialOptions: T[];
  isLoading?: boolean;
  hasMore: boolean;
  label: string;
}

// Build the union of acceptable props, discriminated by multiple/freeSolo
type AutocompleteInfiniteScrollProps<T> =
  | (BaseProps<T> & { multiple?: false; freeSolo?: false } & Omit<
        AutocompleteProps<T, false, false, false>,
        "options" | "loading" | "renderInput" | "multiple" | "freeSolo"
      > & {
        value: ValueType<T, false, false>;
        onChange: (e: any, v: ValueType<T, false, false>) => void;
      })
  | (BaseProps<T> & { multiple: true; freeSolo?: false } & Omit<
        AutocompleteProps<T, true, false, false>,
        "options" | "loading" | "renderInput" | "multiple" | "freeSolo"
      > & {
        value: ValueType<T, true, false>;
        onChange: (e: any, v: ValueType<T, true, false>) => void;
      })
  | (BaseProps<T> & { multiple?: false; freeSolo: true } & Omit<
        AutocompleteProps<T, false, false, true>,
        "options" | "loading" | "renderInput" | "multiple" | "freeSolo"
      > & {
        value: ValueType<T, false, true>;
        onChange: (e: any, v: ValueType<T, false, true>) => void;
      })
  | (BaseProps<T> & { multiple: true; freeSolo: true } & Omit<
        AutocompleteProps<T, true, false, true>,
        "options" | "loading" | "renderInput" | "multiple" | "freeSolo"
      > & {
        value: ValueType<T, true, true>;
        onChange: (e: any, v: ValueType<T, true, true>) => void;
      });

export default function AutocompleteInfiniteScroll<T>(
  props: AutocompleteInfiniteScrollProps<T>
) {
  const {
    loadOptions,
    initialOptions,
    isLoading,
    hasMore,
    label,
    multiple,
    freeSolo,
    onInputChange,
    value,
    onChange,
    ...autocompleteProps
  } = props as any;

  const [inputValue, setInputValue] = useState("");
  const listboxRef = useRef<HTMLUListElement>(null);
  const [isFetching, setIsFetching] = useState(false);

  const fetchingRef = useRef(false);

  const handleScroll = async (e: React.UIEvent<HTMLUListElement>) => {
    const lb = e.currentTarget;
    if (
      lb.scrollTop + lb.clientHeight >= lb.scrollHeight - 20 &&
      hasMore &&
      !isLoading &&
      !fetchingRef.current // on lit la ref, pas l’état React
    ) {
      fetchingRef.current = true; // on bloque immédiatement
      try {
        await loadOptions();
      } finally {
        fetchingRef.current = false;
      }
    }
  };

  // Reset isFetching when inputValue changes (nouvelle recherche)
  useEffect(() => {
    setIsFetching(false);
  }, [inputValue]);

  return (
    <Autocomplete<T, typeof multiple, false, typeof freeSolo>
      {...autocompleteProps}
      clearOnBlur={false}
      selectOnFocus={false}
      multiple={multiple}
      freeSolo={freeSolo}
      options={initialOptions}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(e, v, reason) => {
        setInputValue(v);
        onInputChange?.(e, v, reason);
      }}
      value={value}
      onChange={onChange}
      ListboxProps={{ onScroll: handleScroll, ref: listboxRef }}
      sx={{
        "& .MuiInputBase-root": {
          border: "1px solid hsl(var(--border))",
          borderRadius: "6px",
          py: "2px",
          paddingX: "10px",
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
        },
        "& .MuiInputBase-input": {
          padding: "10px 0",
          color: "hsl(var(--foreground))",
        },
        "& .MuiAutocomplete-tag": {
          height: "24px", // Hauteur des chips
          margin: "2px", // Petit margin pour l'espacement
          bgcolor: "hsl(var(--muted))",
          color: "hsl(var(--foreground))",
          "& .MuiChip-deleteIcon": {
            color: "hsl(var(--muted-foreground))",
            "&:hover": {
              color: "hsl(var(--destructive))",
            },
          },
        },
        "& .MuiAutocomplete-clearIndicator": {
          color: "hsl(var(--muted-foreground))",
        },
        "& .MuiAutocomplete-popupIndicator": {
          color: "hsl(var(--muted-foreground))",
        },
        "& .MuiAutocomplete-listbox": {
          backgroundColor: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
        },
        "& .MuiAutocomplete-option": {
          color: "hsl(var(--foreground))",
          "&:hover": {
            backgroundColor: "hsl(var(--accent))",
            color: "hsl(var(--accent-foreground))",
          },
          "&[aria-selected='true']": {
            backgroundColor: "hsl(var(--accent))",
            color: "hsl(var(--accent-foreground))",
          },
        },
      }}
      renderInput={(params) => (
        <TextFieldLabeled
          {...params}
          label={label}
          autoComplete="off"
          inputProps={{
            ...params.inputProps,
            autoComplete: "off",
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
